/**
 * The messages between the app and the bundle worker (decision 7; part 24a, table E): the app asks for one book's bundle,
 * the worker says how far it has come and answers with the file or why there is none. Each side reads what it receives
 * field by field (Definition of Done, security), so a message that isn't one of these is never taken for one.
 */
import { isId } from '@cockatrice/storage';
import { isSha256 } from '../backup/log.ts';

/** E1: the bundle of a book, made by a device. */
export interface BundleJob {
  readonly kind: 'bundle';
  readonly bookId: string;
  readonly device: string;
}

/** E4: why a bundle wasn't made: the store was upgraded by a newer build, the bundle would pass 4 GiB, or anything else. */
export type BundleFailure = 'outdated' | 'too-large' | 'failed';

/** E2–E4: what the worker sends back. */
export type BundleReply =
  | { readonly kind: 'progress'; readonly done: number; readonly total: number }
  /**
   * E3: the file, the records that couldn't be read, and how many files it holds (part 24c: an import reads at most L3's);
   * its bytes' SHA-256 in hex (part 24e, T8: a bundle saved by hand counts as a backup).
   */
  | { readonly kind: 'done'; readonly file: Blob; readonly unreadable: number; readonly files: number; readonly sha256: string }
  | { readonly kind: 'failed'; readonly reason: BundleFailure };

const FAILURES: readonly BundleFailure[] = ['outdated', 'too-large', 'failed'];

/** The own fields of a plain object with exactly `keys`, or null. */
function fieldsOf(value: unknown, keys: readonly string[]): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === keys.length && keys.every((key) => fields.has(key)) ? fields : null;
}

/** Whether `value` is a whole number from 0. */
function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** E1: the job a message asks for, or null for anything else. */
export function readJob(value: unknown): BundleJob | null {
  const fields = fieldsOf(value, ['kind', 'bookId', 'device']);
  const bookId = fields?.get('bookId');
  const device = fields?.get('device');
  return fields?.get('kind') === 'bundle' && isId(bookId) && isId(device) ? { kind: 'bundle', bookId, device } : null;
}

/** E2–E4: the reply a message is, or null for anything else (E5). */
export function readReply(value: unknown): BundleReply | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'progress') {
    const fields = fieldsOf(value, ['kind', 'done', 'total']);
    const done = fields?.get('done');
    const total = fields?.get('total');
    return isCount(done) && isCount(total) && done <= total ? { kind, done, total } : null;
  }
  if (kind === 'done') {
    const fields = fieldsOf(value, ['kind', 'file', 'unreadable', 'files', 'sha256']);
    const file = fields?.get('file');
    const unreadable = fields?.get('unreadable');
    const files = fields?.get('files');
    const sha256 = fields?.get('sha256');
    return file instanceof Blob && isCount(unreadable) && isCount(files) && isSha256(sha256) ? { kind, file, unreadable, files, sha256 } : null;
  }
  if (kind === 'failed') {
    const reason = FAILURES.find((known) => known === fieldsOf(value, ['kind', 'reason'])?.get('reason'));
    return reason === undefined ? null : { kind, reason };
  }
  return null;
}
